import i18n from '@/utils/i18n';
import React, { useMemo } from 'react';
import {
  createItemsAlreadyExistToast,
  MoveDuplicateItemsActionSheet,
} from './move-duplicate-items-action-sheet';
import {
  EditOrderGuideActions,
  Partition,
  validateExistingItems,
} from '@/hooks/use-edit-order-guide-reducer';
import { ListOption } from '@/components/list-item';
import { NO_PARTITION_NAME } from '@/contexts/multi-order-guide-items-context-provider';
import { Type } from '@/components/toast';
import { useEditOrderGuideActions } from '@/contexts/edit-order-guide-context';
import { useToasts } from '@/contexts/toast-context';

interface Props {
  onClose: () => void;
  partitions: Partition[];
  selectedItemUUIDs: string[];
  selectedPartitionName: string;
  selectedPartitionUUID: string;
}

export const DuplicateItemsActionSheet: React.FC<Props> = ({
  onClose,
  partitions,
  selectedItemUUIDs,
  selectedPartitionUUID,
}) => {
  const dispatch = useEditOrderGuideActions();
  const { showToast } = useToasts();

  const sections = useMemo(() => {
    const partitionOptions = partitions.reduce<ListOption[]>((result, p) => {
      if (p.partitionUUID !== selectedPartitionUUID) {
        result.push({
          title: p.partitionName || NO_PARTITION_NAME,
          value: p.partitionUUID,
        });
      }

      return result;
    }, []);

    return [{ data: partitionOptions }];
  }, [partitions, selectedPartitionUUID]);

  return (
    <MoveDuplicateItemsActionSheet
      dispatch={dispatch}
      onClose={onClose}
      onSelect={(option) => {
        dispatch((actionDispatch, getState) => {
          const targetPartitionUUID = option.value as string;
          const [itemUUIDs, existingItems] = validateExistingItems(
            getState(),
            selectedItemUUIDs,
            selectedPartitionUUID,
            targetPartitionUUID,
          );

          if (existingItems.length) {
            showToast(
              createItemsAlreadyExistToast(existingItems, option.title),
            );
          }

          if (itemUUIDs.length) {
            actionDispatch({
              payload: {
                itemUUIDs,
                sourcePartitionUUID: selectedPartitionUUID,
                targetPartitionUUID,
              },
              type: EditOrderGuideActions.DUPLICATE_ITEMS,
            });

            showToast({
              content: i18n.t('toast.duplicate_items_success', {
                count: itemUUIDs.length,
                groupName: option.title,
              }),
              type: Type.POSITIVE,
            });
          }
        });

        onClose();
      }}
      sections={sections}
      selectPartitionTitle={i18n.t('duplicate_items_in')}
    />
  );
};
